/**
 * Image utility functions for validation and processing
 */

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageValidationResult {
  isValid: boolean;
  warnings: string[];
  recommendations: string[];
}

/**
 * Get image dimensions from a File object
 */
export const getImageDimensions = (file: File): Promise<ImageDimensions> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

/**
 * Validate market card image dimensions (should be square-ish)
 * Recommended: 300x300px or similar square aspect ratio
 */
export const validateMarketCardImage = async (file: File): Promise<ImageValidationResult> => {
  try {
    const dimensions = await getImageDimensions(file);
    const warnings: string[] = [];
    const recommendations: string[] = [];

    // Check aspect ratio - should be close to square (1:1)
    const aspectRatio = dimensions.width / dimensions.height;
    const targetAspectRatio = 1.0;

    if (Math.abs(aspectRatio - targetAspectRatio) > 0.3) {
      warnings.push(`Image aspect ratio (${aspectRatio.toFixed(2)}) is not square. Market cards work best with square images.`);
      recommendations.push('Consider cropping to a square aspect ratio (1:1)');
    }

    // Check minimum size
    if (dimensions.width < 200 || dimensions.height < 200) {
      warnings.push('Image dimensions are quite small. Minimum recommended size is 200x200px.');
      recommendations.push('Use an image at least 300x300px for better quality');
    }

    // Check if too large (performance warning)
    if (dimensions.width > 2000 || dimensions.height > 2000) {
      warnings.push('Image dimensions are very large. This may affect page loading performance.');
      recommendations.push('Consider resizing to around 600x600px maximum');
    }

    return {
      isValid: warnings.length === 0,
      warnings,
      recommendations
    };
  } catch (error) {
    return {
      isValid: false,
      warnings: ['Failed to validate image dimensions'],
      recommendations: ['Ensure the image file is not corrupted']
    };
  }
};

/**
 * Validate featured image dimensions (should be wide for hero display)
 * Recommended: 1200x400px (3:1 aspect ratio)
 */
export const validateFeaturedImage = async (file: File): Promise<ImageValidationResult> => {
  try {
    const dimensions = await getImageDimensions(file);
    const warnings: string[] = [];
    const recommendations: string[] = [];

    // Check aspect ratio - should be wide (around 3:1)
    const aspectRatio = dimensions.width / dimensions.height;
    const targetAspectRatio = 3.0;

    if (Math.abs(aspectRatio - targetAspectRatio) > 1.0) {
      warnings.push(`Image aspect ratio (${aspectRatio.toFixed(2)}) may not work well for featured display. Featured images work best with wider aspect ratios.`);
      recommendations.push('Consider using a 3:1 aspect ratio (e.g., 1200x400px)');
    }

    // Check minimum width for featured display
    if (dimensions.width < 800) {
      warnings.push('Image width is quite small for featured display. Minimum recommended width is 800px.');
      recommendations.push('Use an image at least 1200px wide for better quality');
    }

    // Check minimum height
    if (dimensions.height < 300) {
      warnings.push('Image height is quite small. Minimum recommended height is 300px.');
      recommendations.push('Use an image at least 400px tall');
    }

    // Check if too large (performance warning)
    if (dimensions.width > 3000 || dimensions.height > 1500) {
      warnings.push('Image dimensions are very large. This may affect page loading performance.');
      recommendations.push('Consider resizing to around 2400x800px maximum');
    }

    return {
      isValid: warnings.length === 0,
      warnings,
      recommendations
    };
  } catch (error) {
    return {
      isValid: false,
      warnings: ['Failed to validate image dimensions'],
      recommendations: ['Ensure the image file is not corrupted']
    };
  }
};
